import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import RequireAuth from "@/components/RequireAuth";
import PlatformRoute from "@/components/PlatformRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import HubShell from "@/components/HubShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AppsLauncher from "@/pages/AppsLauncher";
import EmbeddedApp from "@/pages/EmbeddedApp";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import AdminHome from "@/pages/admin/AdminHome";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminApps from "@/pages/admin/AdminApps";
import AdminMatrix from "@/pages/admin/AdminMatrix";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminCalendarTypes from "@/pages/admin/AdminCalendarTypes";
import AdminClients from "@/pages/admin/AdminClients";
import AdminSettings from "@/pages/admin/AdminSettings";
import Calendar from "@/pages/Calendar";
import PlatformHome from "@/pages/platform/PlatformHome";
import PlatformTenants from "@/pages/platform/PlatformTenants";
import PlatformTenantDetail from "@/pages/platform/PlatformTenantDetail";
import PlatformApps from "@/pages/platform/PlatformApps";
import PlatformAudit from "@/pages/platform/PlatformAudit";

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/apps/:appId/embed"
            element={
              <RequireAuth>
                <EmbeddedApp />
              </RequireAuth>
            }
          />
          <Route
            element={
              <RequireAuth>
                <HubShell />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="/apps" element={<AppsLauncher />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route
              path="/admin"
              element={<AdminHome />}
            />
            <Route
              path="/admin/users"
              element={<RequireAuth perm="users.read"><AdminUsers /></RequireAuth>}
            />
            {/* Tenant-scoped roles + permissions matrix. Tenant_admin reaches
                these via the TENANT sidebar; platform admins bypass perms.
                The /platform/roles + /platform/permissions URLs no longer
                exist — both legacy paths redirect to the tenant-scoped ones. */}
            <Route
              path="/admin/roles"
              element={<RequireAuth perm="roles.read"><AdminRoles /></RequireAuth>}
            />
            <Route
              path="/admin/matrix"
              element={<RequireAuth perm="roles.read"><AdminMatrix /></RequireAuth>}
            />
            <Route path="/platform/roles" element={<Navigate to="/admin/roles" replace />} />
            <Route path="/platform/permissions" element={<Navigate to="/admin/matrix" replace />} />
            <Route
              path="/admin/apps"
              element={<RequireAuth perm="app_registry.read"><AdminApps /></RequireAuth>}
            />
            <Route
              path="/admin/vehicles"
              element={<RequireAuth perm="calendar.admin"><AdminVehicles /></RequireAuth>}
            />
            <Route
              path="/admin/calendar-types"
              element={<RequireAuth perm="calendar.admin"><AdminCalendarTypes /></RequireAuth>}
            />
            <Route
              path="/admin/clients"
              element={<RequireAuth perm="calendar.admin"><AdminClients /></RequireAuth>}
            />
            <Route
              path="/admin/settings"
              element={<RequireAuth perm="tenant.manage"><AdminSettings /></RequireAuth>}
            />
            <Route
              path="/calendar"
              element={<RequireAuth perm="calendar.read"><Calendar /></RequireAuth>}
            />
            <Route
              path="/reports"
              element={<RequireAuth perm="reports.read"><Reports /></RequireAuth>}
            />
            <Route
              path="/platform"
              element={<PlatformRoute><PlatformHome /></PlatformRoute>}
            />
            <Route
              path="/platform/tenants"
              element={<PlatformRoute><PlatformTenants /></PlatformRoute>}
            />
            <Route
              path="/platform/tenants/:slug"
              element={<PlatformRoute><PlatformTenantDetail /></PlatformRoute>}
            />
            <Route
              path="/platform/apps"
              element={<PlatformRoute><PlatformApps /></PlatformRoute>}
            />
            <Route
              path="/platform/audit"
              element={<PlatformRoute><PlatformAudit /></PlatformRoute>}
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{ className: "font-sans text-sm" }}
        />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
